// ===================================
// LOGIN PAGE - ENHANCED
// frontend/src/pages/Login.js
// Supports Google OAuth + Magic Login
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Use both login (for credentials) and setAuth (for OAuth)
  const { login, setAuth, isAuthenticated } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [authProvider, setAuthProvider] = useState(null); // Track if user needs Google

  // Check for redirect messages
  useEffect(() => {
    const message = searchParams.get('message');
    if (message === 'magic_sent') {
      setError('Check your email for a magic login link!');
    }
  }, [searchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Fetch Google client ID
  useEffect(() => {
    const fetchGoogleConfig = async () => {
      try {
        const response = await api.get('/auth/google/config');
        if (response.data.clientId && response.data.enabled) {
          setGoogleClientId(response.data.clientId);
        }
      } catch (err) {
        console.log('Google OAuth not configured');
      }
    };
    fetchGoogleConfig();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAuthProvider(null);
    setLoading(true);

    try {
      // Use the login function from AuthContext (which makes API call)
      const result = await login({ email, password });
      
      if (result.success) {
        navigate('/app/dashboard');
      } else {
        if (result.authProvider === 'google') {
          setAuthProvider('google');
          setError('This account uses Google sign-in. Please use the Google button below.');
        } else {
          setError(result.error || 'Invalid email or password');
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.authProvider === 'google') {
        setAuthProvider('google');
        setError('This account uses Google sign-in. Please use the Google button below.');
      } else {
        setError(errorData?.message || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google sign-in success
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential
      });

      // Use setAuth (not login) - just store token and user, no API call
      setAuth(response.data.token, response.data.user);
      navigate('/app/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo */}
        <div className="login-logo">
          <Link to="/">
            <span className="logo-icon">🎯</span>
            <span className="logo-text">Career-Coach.AI</span>
          </Link>
        </div>

        <h1>Welcome back</h1>
        <p className="subtitle">Sign in to continue your interview practice</p>

        {/* Error Message */}
        {error && (
          <div className={`login-error ${error.includes('magic') ? 'info' : ''}`}>
            <span className="error-icon">{error.includes('magic') ? '📬' : '⚠️'}</span>
            {error}
          </div>
        )}

        {/* Google Sign-In */}
        {googleClientId && (
          <GoogleOAuthProvider clientId={googleClientId}>
            <div className="google-signin-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="filled_black"
                size="large"
                text="signin_with"
                shape="rectangular"
                width="100%"
              />
            </div>
          </GoogleOAuthProvider>
        )}

        {/* Show Google prompt if user needs it */}
        {authProvider === 'google' && googleClientId && (
          <div className="google-hint">
            <span>👆</span> Use the Google button above to sign in
          </div>
        )}

        {googleClientId && <div className="divider"><span>or</span></div>}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <div className="label-row">
              <label htmlFor="password">Password</label>
              <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
            </div>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="register-link">
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;